import { FunctionComponent, memo } from "react";
import { IProps } from "./assignment-details.types";
import { useEmailAssignments } from "./assignment-details.utilities";
import { DetailsSection, DetailsSectionTitle } from "@edgetier/client-components";
import AssignmentDetailsField from "./assignment-details-field";
import { SpinnerUntil } from "@edgetier/components";
import { IEmailAssignment } from "@edgetier/types";
import { compareDesc } from "date-fns";
import AssignAgents from "components/search-emails/assign-agents";
import "./assignment-details.scss";

/**
 * Show assignment details for an email.
 * @param props.emailId The ID of the email who's details we're showing.
 */
const AssignmentDetails: FunctionComponent<IProps> = ({ emailId, interactionCompleted }) => {
    const { data, isLoading, refetch } = useEmailAssignments(
        emailId,
        {},
        {
            select: (assignments) => {
                return assignments.sort((one, two) =>
                    compareDesc(new Date(one.createDateTime), new Date(two.createDateTime))
                );
            },
        }
    );

    const onSuccess = () => {
        refetch();
    };

    return (
        <div className="assignment-details">
            <DetailsSection>
                <DetailsSectionTitle title="Assignments">
                    <AssignAgents emailIds={[emailId]} onSuccess={onSuccess} />
                </DetailsSectionTitle>
                <SpinnerUntil<IEmailAssignment[]> isReady={!isLoading} data={data}>
                    {(data) =>
                        data.length > 0 ? (
                            <table>
                                <tbody>
                                    {data.map((assignment, index) => (
                                        <AssignmentDetailsField
                                            assignment={assignment}
                                            interactionCompleted={interactionCompleted}
                                            key={`${assignment.emailId}-${assignment.userId}-${index}`}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="assignment-details__no-assignments">
                                No agents have been assigned to this email.
                            </div>
                        )
                    }
                </SpinnerUntil>
            </DetailsSection>
        </div>
    );
};

export default memo(AssignmentDetails);
